import { FormEvent, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'
import toast from 'react-hot-toast'
import { siteContentQueryKey, useSiteContent } from '@/hooks/useSiteContent'
import type { SiteContent } from '@/lib/siteContent'
import { defaultSiteContent } from '@/lib/siteContent'
import { isSupabaseConfigured, supabase } from '@/lib/supabase'

type TextFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  multiline?: boolean
}

const TextField = ({ label, value, onChange, multiline }: TextFieldProps) => {
  return (
    <label>
      {label}
      {multiline ? (
        <textarea value={value} rows={4} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input value={value} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  )
}

const AdminPage = () => {
  const queryClient = useQueryClient()
  const { content, isLoading } = useSiteContent()
  const [draft, setDraft] = useState<SiteContent>(content)
  const [session, setSession] = useState<Session | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraft(content)
  }, [content])

  useEffect(() => {
    if (!supabase) {
      setAuthReady(true)
      return
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setAuthReady(true)
    })

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
    })

    return () => {
      data.subscription.unsubscribe()
    }
  }, [])

  const updateDraft = (path: Array<string | number>, value: string) => {
    setDraft((previous) => {
      const next = structuredClone(previous)
      let cursor = next as unknown as Record<string, unknown> | unknown[]

      for (const segment of path.slice(0, -1)) {
        const child = Array.isArray(cursor) ? cursor[segment as number] : cursor[segment as string]
        cursor = child as Record<string, unknown> | unknown[]
      }

      const finalSegment = path[path.length - 1]

      if (Array.isArray(cursor)) {
        cursor[finalSegment as number] = value
      } else {
        cursor[finalSegment as string] = value
      }

      return next
    })
  }

  const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!supabase) {
      return
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      toast.error(error.message)
      return
    }

    toast.success('Signed in')
  }

  const handleSignOut = async () => {
    if (!supabase) {
      return
    }

    await supabase.auth.signOut()
    toast.success('Signed out')
  }

  const handleReset = () => {
    setDraft(content)
  }

  const handleSave = async () => {
    if (!supabase || !session) {
      return
    }

    setSaving(true)

    const { error } = await supabase.from('site_content').upsert({
      key: 'site',
      content: draft,
      updated_by: session.user.id,
      updated_at: new Date().toISOString(),
    })

    setSaving(false)

    if (error) {
      toast.error(error.message)
      return
    }

    await queryClient.invalidateQueries({ queryKey: siteContentQueryKey })
    toast.success('Website content updated')
  }

  if (!isSupabaseConfigured) {
    return (
      <main className="admin-page">
        <section className="admin-empty-state">
          <p className="eyebrow">Admin setup</p>
          <h1>Connect Supabase to enable editing.</h1>
          <p>
            Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your local environment and in
            Vercel. Until then, the website will keep using the built-in content.
          </p>
        </section>
      </main>
    )
  }

  if (!authReady || isLoading) {
    return (
      <main className="admin-page">
        <section className="admin-empty-state">
          <p className="eyebrow">Admin</p>
          <h1>Loading content...</h1>
        </section>
      </main>
    )
  }

  if (!session) {
    return (
      <main className="admin-page admin-page--centered">
        <form className="admin-login" onSubmit={handleSignIn}>
          <p className="eyebrow">Shelf Discovery Admin</p>
          <h1>Sign in to edit the website.</h1>
          <label>
            Email
            <input type="email" value={email} required onChange={(event) => setEmail(event.target.value)} />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              required
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <button type="submit">Sign in</button>
        </form>
      </main>
    )
  }

  return (
    <main className="admin-page">
      <header className="admin-header">
        <div>
          <p className="eyebrow">Shelf Discovery Admin</p>
          <h1>Edit website content</h1>
        </div>
        <div className="admin-actions">
          <button type="button" onClick={handleReset}>Reset</button>
          <button type="button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
          <button type="button" onClick={handleSignOut}>Sign out</button>
        </div>
      </header>

      <section className="admin-grid">
        <fieldset>
          <legend>Navigation</legend>
          <TextField label="Brand name" value={draft.nav.brand} onChange={(value) => updateDraft(['nav', 'brand'], value)} />
          <TextField
            label="Shelf owners CTA"
            value={draft.nav.shelfOwnersCta}
            onChange={(value) => updateDraft(['nav', 'shelfOwnersCta'], value)}
          />
          <TextField
            label="Brands CTA"
            value={draft.nav.brandsCta}
            onChange={(value) => updateDraft(['nav', 'brandsCta'], value)}
          />
        </fieldset>

        <fieldset>
          <legend>Homepage hero</legend>
          <TextField label="Eyebrow" value={draft.hero.eyebrow} onChange={(value) => updateDraft(['hero', 'eyebrow'], value)} />
          <TextField label="Title" value={draft.hero.title} onChange={(value) => updateDraft(['hero', 'title'], value)} multiline />
          <TextField label="Body" value={draft.hero.body} onChange={(value) => updateDraft(['hero', 'body'], value)} multiline />
          <TextField
            label="Image note eyebrow"
            value={draft.hero.noteEyebrow}
            onChange={(value) => updateDraft(['hero', 'noteEyebrow'], value)}
          />
          <TextField
            label="Image note title"
            value={draft.hero.noteTitle}
            onChange={(value) => updateDraft(['hero', 'noteTitle'], value)}
            multiline
          />
        </fieldset>

        <fieldset>
          <legend>Images</legend>
          <TextField
            label="Hero image URL"
            value={draft.media.heroImageUrl}
            onChange={(value) => updateDraft(['media', 'heroImageUrl'], value)}
          />
          <TextField
            label="Shelf owner card image URL"
            value={draft.media.shelfOwnerImageUrl}
            onChange={(value) => updateDraft(['media', 'shelfOwnerImageUrl'], value)}
          />
          <TextField
            label="Brand card image URL"
            value={draft.media.brandImageUrl}
            onChange={(value) => updateDraft(['media', 'brandImageUrl'], value)}
          />
          <p className="admin-note">
            Leave image URL fields blank to use the built-in website images. Supabase Storage public
            URLs work well here.
          </p>
        </fieldset>

        <fieldset>
          <legend>Metrics</legend>
          {draft.metrics.map((metric, index) => (
            <div className="admin-repeat" key={`metric-${index.toString()}`}>
              <TextField
                label={`Metric ${index + 1} title`}
                value={metric.title}
                onChange={(value) => updateDraft(['metrics', index, 'title'], value)}
              />
              <TextField
                label={`Metric ${index + 1} body`}
                value={metric.body}
                onChange={(value) => updateDraft(['metrics', index, 'body'], value)}
              />
            </div>
          ))}
        </fieldset>

        <fieldset>
          <legend>Partner paths</legend>
          <TextField
            label="Section eyebrow"
            value={draft.partners.eyebrow}
            onChange={(value) => updateDraft(['partners', 'eyebrow'], value)}
          />
          <TextField
            label="Section title"
            value={draft.partners.title}
            onChange={(value) => updateDraft(['partners', 'title'], value)}
            multiline
          />
          <div className="admin-repeat">
            <TextField
              label="Shelf owner label"
              value={draft.partners.realEstate.label}
              onChange={(value) => updateDraft(['partners', 'realEstate', 'label'], value)}
            />
            <TextField
              label="Shelf owner title"
              value={draft.partners.realEstate.title}
              onChange={(value) => updateDraft(['partners', 'realEstate', 'title'], value)}
            />
            <TextField
              label="Shelf owner body"
              value={draft.partners.realEstate.body}
              onChange={(value) => updateDraft(['partners', 'realEstate', 'body'], value)}
              multiline
            />
          </div>
          <div className="admin-repeat">
            <TextField
              label="Brand label"
              value={draft.partners.brands.label}
              onChange={(value) => updateDraft(['partners', 'brands', 'label'], value)}
            />
            <TextField
              label="Brand title"
              value={draft.partners.brands.title}
              onChange={(value) => updateDraft(['partners', 'brands', 'title'], value)}
            />
            <TextField
              label="Brand body"
              value={draft.partners.brands.body}
              onChange={(value) => updateDraft(['partners', 'brands', 'body'], value)}
              multiline
            />
          </div>
        </fieldset>

        <fieldset>
          <legend>Shelf owners page</legend>
          <TextField
            label="Eyebrow"
            value={draft.realEstatePage.eyebrow}
            onChange={(value) => updateDraft(['realEstatePage', 'eyebrow'], value)}
          />
          <TextField
            label="Title"
            value={draft.realEstatePage.title}
            onChange={(value) => updateDraft(['realEstatePage', 'title'], value)}
            multiline
          />
          <TextField
            label="Body"
            value={draft.realEstatePage.body}
            onChange={(value) => updateDraft(['realEstatePage', 'body'], value)}
            multiline
          />
          <TextField
            label="Form eyebrow"
            value={draft.realEstatePage.formEyebrow}
            onChange={(value) => updateDraft(['realEstatePage', 'formEyebrow'], value)}
          />
          <TextField
            label="Form title"
            value={draft.realEstatePage.formTitle}
            onChange={(value) => updateDraft(['realEstatePage', 'formTitle'], value)}
          />
        </fieldset>

        <fieldset>
          <legend>Brands page</legend>
          <TextField
            label="Eyebrow"
            value={draft.brandsPage.eyebrow}
            onChange={(value) => updateDraft(['brandsPage', 'eyebrow'], value)}
          />
          <TextField
            label="Title"
            value={draft.brandsPage.title}
            onChange={(value) => updateDraft(['brandsPage', 'title'], value)}
            multiline
          />
          <TextField
            label="Body"
            value={draft.brandsPage.body}
            onChange={(value) => updateDraft(['brandsPage', 'body'], value)}
            multiline
          />
          <TextField
            label="Form eyebrow"
            value={draft.brandsPage.formEyebrow}
            onChange={(value) => updateDraft(['brandsPage', 'formEyebrow'], value)}
          />
          <TextField
            label="Form title"
            value={draft.brandsPage.formTitle}
            onChange={(value) => updateDraft(['brandsPage', 'formTitle'], value)}
          />
        </fieldset>

        <fieldset>
          <legend>Footer</legend>
          <TextField
            label="Eyebrow"
            value={draft.footer.eyebrow}
            onChange={(value) => updateDraft(['footer', 'eyebrow'], value)}
          />
          <TextField
            label="Title"
            value={draft.footer.title}
            onChange={(value) => updateDraft(['footer', 'title'], value)}
            multiline
          />
          <TextField
            label="Body"
            value={draft.footer.body}
            onChange={(value) => updateDraft(['footer', 'body'], value)}
            multiline
          />
          <TextField
            label="Contact label"
            value={draft.footer.contactEyebrow}
            onChange={(value) => updateDraft(['footer', 'contactEyebrow'], value)}
          />
          <TextField label="Email" value={draft.footer.email} onChange={(value) => updateDraft(['footer', 'email'], value)} />
        </fieldset>

        <fieldset>
          <legend>Recovery</legend>
          <p className="admin-note">
            Need to recover the original copy? Use the button below to load the built-in defaults,
            then save.
          </p>
          <button type="button" onClick={() => setDraft(defaultSiteContent)}>Load default content</button>
        </fieldset>
      </section>
    </main>
  )
}

export default AdminPage
